export type RankingPrize = { place: number; amount_cents: number };

// How often open ranking pages reload the standings. Tracked views refresh
// twice a day and a reviewer's own decisions refetch right away, while each
// reload of a large ranking costs the API about a second (2026-10-29).
export const RANKING_POLL_INTERVAL_MS = 5 * 60_000;
export type RankingPrizeGroup = { startPlace: number; endPlace: number; count: number; amount_cents: number };
export type RankingConfig = {
  version: 1;
  min_total_views: number;
  prizes: RankingPrize[];
  report_deadline: string | null;
  timezone: string;
  payout_destination: 'external' | 'wallet';
  auto_approve_clips?: boolean;
  organizer_whatsapp?: string | null;
};

export type CampaignCompetitionFields = {
  campaign_type?: string | null;
  ranking_config?: unknown;
};

export const RANKING_PLATFORMS = ['instagram', 'tiktok', 'youtube', 'facebook'] as const;
export type RankingPlatform = typeof RANKING_PLATFORMS[number];

export type RankingReportInput = {
  id?: string;
  platform: RankingPlatform;
  url: string;
  profile_url: string;
  published_at: string;
  reported_views: string;
  evidence_url: string;
};

export function defaultRankingConfig(): RankingConfig {
  return { version: 1, min_total_views: 0, prizes: [], report_deadline: null, timezone: 'America/Sao_Paulo', payout_destination: 'external', auto_approve_clips: false, organizer_whatsapp: null };
}

export function normalizeRankingConfig(value: unknown): RankingConfig {
  const raw = value && typeof value === 'object' ? value as Partial<RankingConfig> : {};
  return {
    ...defaultRankingConfig(),
    min_total_views: typeof raw.min_total_views === 'number' ? raw.min_total_views : 0,
    prizes: Array.isArray(raw.prizes) ? raw.prizes.map(prize => ({ ...prize })) : [],
    report_deadline: typeof raw.report_deadline === 'string' ? raw.report_deadline : null,
    timezone: typeof raw.timezone === 'string' ? raw.timezone : 'America/Sao_Paulo',
    payout_destination: raw.payout_destination === 'wallet' ? 'wallet' : 'external',
    auto_approve_clips: raw.auto_approve_clips === true,
    organizer_whatsapp: typeof raw.organizer_whatsapp === 'string' && raw.organizer_whatsapp.trim() ? raw.organizer_whatsapp.trim() : null,
  };
}

export function rankingPrizePool(config: RankingConfig): number {
  return config.prizes.reduce((sum, prize) => sum + prize.amount_cents, 0);
}

/** Presentation only; individual awards stay unchanged for configuration and settlement. */
export function groupRankingPrizes(prizes: readonly RankingPrize[]): RankingPrizeGroup[] {
  const groups: RankingPrizeGroup[] = [];
  for (const prize of prizes) {
    const previous = groups.at(-1);
    if (previous && prize.place === previous.endPlace + 1 && prize.amount_cents === previous.amount_cents) {
      previous.endPlace = prize.place;
      previous.count += 1;
    } else {
      groups.push({ startPlace: prize.place, endPlace: prize.place, count: 1, amount_cents: prize.amount_cents });
    }
  }
  return groups;
}

export function rankingConfigError(config: RankingConfig, endDate?: string, currency = 'BRL'): string | null {
  if (config.auto_approve_clips !== undefined && typeof config.auto_approve_clips !== 'boolean') return 'invalidAutoApproval';
  if (config.organizer_whatsapp != null && config.organizer_whatsapp !== ''
    && (typeof config.organizer_whatsapp !== 'string' || !/^\+[1-9]\d{6,14}$/.test(config.organizer_whatsapp))) return 'invalidOrganizerWhatsapp';
  if (!Number.isSafeInteger(config.min_total_views) || config.min_total_views < 0 || config.min_total_views > 1000000000000) return 'invalidMinimum';
  if (config.prizes.length === 0 || config.prizes.length > 100
    || config.prizes.some((prize, i) => prize.place !== i + 1 || !Number.isSafeInteger(prize.amount_cents) || prize.amount_cents <= 0)
    || rankingPrizePool(config) > 2147483647) return 'invalidPrizes';
  try { new Intl.DateTimeFormat('en', { timeZone: config.timezone }); } catch { return 'invalidTimezone'; }
  if (config.report_deadline && (!Number.isFinite(Date.parse(config.report_deadline))
    || (endDate && Date.parse(config.report_deadline) <= Date.parse(endDate)))) return 'invalidDeadline';
  if (config.payout_destination === 'wallet' && currency !== 'BRL') return 'walletBrlOnly';
  return null;
}

function isHttps(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch { return false; }
}

export function rankingReportError(input: RankingReportInput): string | null {
  if (!/^\d{1,13}$/.test(input.reported_views) || BigInt(input.reported_views) > 1000000000000n) return 'invalidViews';
  if (!RANKING_PLATFORMS.includes(input.platform) || ![input.url, input.profile_url, input.evidence_url].every(isHttps)
    || !Number.isFinite(Date.parse(input.published_at))) return 'invalidReport';
  return null;
}

export function toLocalDatetime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromLocalDatetime(value: string): string | null {
  const timestamp = new Date(value);
  return value && Number.isFinite(timestamp.getTime()) ? timestamp.toISOString() : null;
}

/** The form edits the last included minute; the API stores its exclusive end. */
export function rankingEndFromLocal(value: string): string | null {
  const timestamp = fromLocalDatetime(value);
  return timestamp ? new Date(Date.parse(timestamp) + 60000).toISOString() : null;
}

export function rankingEndToLocal(value: string | null | undefined): string {
  if (!value || !Number.isFinite(Date.parse(value))) return '';
  return toLocalDatetime(new Date(Date.parse(value) - 60000).toISOString());
}
